import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

type Language = 'en' | 'ar';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    'nav.home': 'Home',
    'nav.cats': 'Adopt',
    'nav.products': 'Products',
    'nav.cart': 'Cart',
    'nav.login': 'Sign In',
    'nav.register': 'Get Started',
    'nav.profile': 'Profile',
    'nav.orders': 'Orders',
    'nav.wishlist': 'Wishlist',
    'nav.about': 'About Us',
    'nav.logout': 'Sign Out',
    'nav.admin': 'Admin',
    'nav.admin.dashboard': 'Dashboard',
    'nav.admin.cats': 'Manage Cats',
    'nav.admin.products': 'Manage Products',
    'nav.admin.orders': 'Orders',
    'nav.admin.users': 'Users',
    'nav.admin.settings': 'Settings',
    'nav.backToStore': 'Back to Store',
    'common.cats': 'Cats',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.retry': 'Retry',
    'common.backToHome': 'Back to Home',
    'common.goBack': 'Go Back',
    'common.linkCopied': 'Link copied!',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.clearAll': 'Clear all',
    'common.confirm': 'Confirm',
    'common.ok': 'OK',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.or': 'Or',
    'common.and': 'And',
    'common.of': 'of',
    'common.items': 'items',
    'common.item': 'item',
    'common.total': 'Total',
    'common.subtotal': 'Subtotal',
    'common.shipping': 'Shipping',
    'common.tax': 'Tax',
    'common.free': 'Free',
    'common.price': 'Price',
    'common.stock': 'Stock',
    'common.category': 'Category',
    'common.description': 'Description',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.password': 'Password',
    'common.confirmPassword': 'Confirm Password',
    'common.age': 'Age',
    'common.sex': 'Sex',
    'common.male': 'Male',
    'common.female': 'Female',
    'common.status': 'Status',
    'common.actions': 'Actions',
    'common.listed': 'Listed',
    'common.updated': 'Updated',
    'common.created': 'Created',
    'common.available': 'Available',
    'common.adopted': 'Adopted',
    'common.sold': 'Sold',
    'common.pending': 'Pending',
    'common.inStock': 'In Stock',
    'common.lowStock': 'Low Stock',
    'common.outOfStock': 'Out of Stock',
    'common.forSale': 'For Sale',
    'common.forAdoption': 'For Adoption',
    'common.sale': 'Sale',
    'common.adoption': 'Adoption',
    'common.adoptionFee': 'Adoption Fee',
    'common.feeWaived': 'Fee waived for qualified adopters',
    'common.months': 'months',
    'common.month': 'month',
    'common.monthsOld': '{count} months old',
    'common.filtered': '(filtered)',
    'common.showing': 'Showing {count} of {total}',
    'common.egp': 'EGP',
    'common.currency': 'EGP',
    'common.allStatus': 'All Status',
    'common.allTypes': 'All Types',
    'common.type': 'Type',
    'common.cat': 'Cat',
    'common.product': 'Product',
    'common.approved': 'Approved',
    'common.rejected': 'Rejected',
    'common.preview': 'Preview',
    'common.share': 'Share',
    'common.whatsapp': 'WhatsApp',
    'common.searchCats': 'Search cats by name...',
    'common.clearSearch': 'Clear search',
    'common.viewOnStore': 'View on store',
    'common.editCat': 'Edit cat',
    'common.deleteCat': 'Delete cat',
    'common.editProduct': 'Edit product',
    'common.deleteProduct': 'Delete product',
    'common.addToFavorites': 'Add to favorites',
    'common.removeFromFavorites': 'Remove from favorites',
    'common.noCatsFound': 'No cats found',
    'common.noProductsFound': 'No products found',
    'common.tryAdjusting': 'Try adjusting your search or filters.',
    'common.ageMonths': '{count} months',
    'home.title': 'Adopt a cat. Save a life.',
    'home.subtitle': 'Every year, thousands of cats need loving homes. Adopt a cat today or shop premium products for your feline friend.',
    'home.browseCats': 'Adopt Now',
    'home.shopProducts': 'Shop Products',
    'home.trustedBy': 'Trusted by 10,000+ cat lovers',
    'home.features.adoptionFirst': 'Adoption First',
    'home.features.adoptionFirstDesc': 'We prioritize finding loving forever homes for every cat in our care.',
    'home.features.vetChecked': 'Vet Checked',
    'home.features.vetCheckedDesc': 'All cats are thoroughly examined before listing.',
    'home.features.shipping': 'Reliable Shipping',
    'home.features.shippingDesc': 'Fast and safe delivery straight to your door with secure packaging.',
    'home.catsAvailable': 'Cats for Adoption',
    'home.catsSubtitle': 'These cats are waiting for a loving home',
    'home.adoptCatsCta': 'Adopt a Cat',
    'home.forSaleCats': 'Cats for Sale',
    'home.forSaleCatsSubtitle': 'Purebred and specialty cats available for purchase',
    'home.featuredProducts': 'Featured Products',
    'home.productsSubtitle': 'Essentials for your feline friend',
    'home.viewAll': 'View All',
    'home.noListings': 'No listings yet',
    'home.noListingsDesc': 'Be the first to list a cat or product. Start building our community!',
    'home.addCat': 'Add a Cat',
    'home.addProduct': 'Add a Product',
    'home.whatsappTitle': 'Have a Cat for Adoption?',
    'home.whatsappDesc': 'List your cat on Cat Care and find them a loving forever home. Contact us on WhatsApp to get started.',
    'home.whatsappCta': 'Contact on WhatsApp',
    'home.listCatCta': 'List Your Cat for Adoption',
    'home.whatsappMsg': 'Hi Cat Care! I would like to list my cat for adoption on your platform.',
    'home.whatsappMsgAr': 'مرحباً كت كير! عايز أعرض قطة للتبني على منصتكم.',
    'cats.title': 'Cats for',
    'cats.toggleAdoption': 'Adoption',
    'cats.toggleSale': 'Sale',
    'cats.toggleAll': 'All',
    'cats.found': 'cats found',
    'cats.cat': 'cat',
    'cats.sortBy': 'Sort by',
    'cats.sort.newest': 'Newest First',
    'cats.sort.oldest': 'Oldest First',
    'cats.sort.priceAsc': 'Price: Low to High',
    'cats.sort.priceDesc': 'Price: High to Low',
    'cats.sort.ageAsc': 'Age: Youngest First',
    'cats.sort.ageDesc': 'Age: Oldest First',
    'cats.filter.listingType': 'Listing Type',
    'cats.filter.allTypes': 'All Types',
    'cats.filter.sale': 'For Sale',
    'cats.filter.adoption': 'For Adoption',
    'cats.filter.sex': 'Sex',
    'cats.filter.all': 'All',
    'cats.noCatsFound': 'No cats found',
    'cats.noCatsFoundFiltered': 'Try adjusting your filters to find more cats.',
    'cats.noCatsAvailable': 'No cats are currently available. Check back soon!',
    'cats.clearFilters': 'Clear Filters',
    'products.title': 'Products',
    'products.found': 'products found',
    'products.product': 'product',
    'products.sortBy': 'Sort by',
    'products.sort.newest': 'Newest First',
    'products.sort.oldest': 'Oldest First',
    'products.sort.priceAsc': 'Price: Low to High',
    'products.sort.priceDesc': 'Price: High to Low',
    'products.sort.nameAsc': 'Name: A to Z',
    'products.sort.nameDesc': 'Name: Z to A',
    'products.search': 'Search products...',
    'products.filter.category': 'Category',
    'products.filter.allCategories': 'All Categories',
    'products.filter.stock': 'Stock Status',
    'products.filter.allStock': 'All Stock',
    'products.filter.inStock': 'In Stock (10+)',
    'products.filter.lowStock': 'Low Stock (1-9)',
    'products.filter.outOfStock': 'Out of Stock',
    'products.noProductsFound': 'No products found',
    'products.noProductsFoundFiltered': 'Try adjusting your filters or search terms.',
    'products.noProductsAvailable': 'No products are currently available.',
    'products.clearFilters': 'Clear Filters',
    'catDetail.backToCats': 'Back to Cats',
    'catDetail.about': 'About',
    'catDetail.details': 'Details',
    'catDetail.age': 'Age',
    'catDetail.listed': 'Listed',
    'catDetail.location': 'Location',
    'catDetail.availableLocally': 'Available locally',
    'catDetail.id': 'ID',
    'catDetail.noDescription': 'No description available for this cat.',
    'catDetail.purchaseDesc': 'Ready to purchase? This cat will be reserved for you upon payment.',
    'catDetail.adoptionDesc': 'Interested in adoption? Contact us to start the adoption process.',
    'catDetail.addToCart': 'Add to Cart - Purchase',
    'catDetail.startAdoption': 'Start Adoption Process',
    'catDetail.notAvailable': 'Not Available',
    'catDetail.addedToCart': 'Added to Cart',
    'catDetail.localPickup': 'Local Pickup',
    'catDetail.arrangePickup': 'Arrange pickup',
    'catDetail.healthGuaranteed': 'Health Guaranteed',
    'catDetail.vetChecked': 'Vet checked',
    'catDetail.viewImage': 'View image',
    'catDetail.orderNow': 'Order Now',
    'catDetail.adoptNow': 'Adopt Now',
    'productDetail.backToProducts': 'Back to Products',
    'productDetail.description': 'Description',
    'productDetail.quantity': 'Quantity',
    'productDetail.subtotal': 'Subtotal',
    'productDetail.addToCart': 'Add to Cart',
    'productDetail.updateCart': 'Update Cart',
    'productDetail.outOfStock': 'Out of Stock',
    'productDetail.inCart': 'In Cart',
    'productDetail.alreadyInCart': 'Already in Cart',
    'productDetail.addedToCart': 'Added to cart',
    'productDetail.category': 'Category',
    'productDetail.stockAvailable': 'Stock Available',
    'productDetail.units': 'units',
    'productDetail.sku': 'SKU',
    'productDetail.added': 'Listed',
    'productDetail.viewImage': 'View image',
    'productDetail.decreaseQty': 'Decrease quantity',
    'productDetail.increaseQty': 'Increase quantity',
    'productDetail.orderNow': 'Order Now',
    'productDetail.productDetails': 'Product Details',
    'productDetail.qualityGuaranteed': 'Quality Guaranteed',
    'productDetail.vetChecked': 'Vet Checked',
    'productDetail.premiumProducts': 'Premium products',
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty',
    'cart.emptyDesc': 'Looks like you haven\'t added anything yet. Start shopping for your feline friend!',
    'cart.browseCats': 'Browse Cats',
    'cart.shopProducts': 'Shop Products',
    'cart.cats': 'Cats',
    'cart.products': 'Products',
    'cart.subtotal': 'Subtotal',
    'cart.shipping': 'Shipping',
    'cart.tax': 'Tax',
    'cart.total': 'Total',
    'cart.freeShipping': 'You qualify for free shipping!',
    'cart.addForFreeShipping': 'Add ${amount} more for free shipping!',
    'cart.checkout': 'Proceed to Checkout',
    'cart.clearCart': 'Clear Cart',
    'cart.continueShopping': 'Continue Shopping',
    'cart.secure': 'Secure',
    'cart.fast': 'Fast',
    'cart.placeOrder': 'Place Order',
    'cart.adoptionRequests': 'Adoption Request',
    'cart.purchaseRequests': 'Order Request',
    'cart.removeItem': 'Remove item',
    'auth.welcomeBack': 'Welcome back',
    'auth.signInDesc': 'Sign in to your account to continue',
    'auth.createAccount': 'Create your account',
    'auth.registerDesc': 'Join our community of cat lovers',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.signIn': 'Sign In',
    'auth.createAccountBtn': 'Create Account',
    'auth.forgotPassword': 'Forgot password?',
    'auth.dontHaveAccount': 'Don\'t have an account?',
    'auth.alreadyHaveAccount': 'Already have an account?',
    'auth.createOne': 'Create one',
    'auth.signInLink': 'Sign in',
    'auth.termsAgree': 'By creating an account, you agree to our',
    'auth.termsOfService': 'Terms of Service',
    'auth.privacyPolicy': 'Privacy Policy',
    'auth.passwordMismatch': 'Passwords do not match',
    'auth.passwordTooShort': 'Password must be at least 8 characters',
    'auth.invalidCredentials': 'Invalid email or password',
    'auth.registrationFailed': 'Registration failed. Please try again.',
    'admin.deleteProduct': 'Delete Product',
    'admin.deleteProductConfirm': 'This product will be permanently deleted. This action cannot be undone.',
    'admin.deleteCat': 'Delete Cat',
    'admin.deleteCatConfirm': 'This cat will be permanently deleted. This action cannot be undone.',
    'admin.delete': 'Delete',
    'admin.dashboard': 'Dashboard',
    'admin.welcome': 'Welcome back! Here\'s what\'s happening with your store.',
    'admin.loadFailed': 'Failed to load dashboard',
    'admin.loadFailedDesc': 'We couldn\'t fetch your admin data. Please check your connection and try again.',
    'admin.totalCats': 'Total Cats',
    'admin.availableCats': 'Available',
    'admin.pendingCats': 'Pending Adoption',
    'admin.totalProducts': 'Products',
    'admin.lowStockProducts': 'Low Stock',
    'admin.addCat': 'Add Cat',
    'admin.addProduct': 'Add Product',
    'admin.viewOrders': 'View Orders',
    'admin.manageUsers': 'Manage Users',
    'admin.manageCats': 'Manage Cats',
    'admin.totalCatsCount': '{count} cats total',
    'admin.manageProducts': 'Manage Products',
    'admin.recentActivity': 'Recent Activity',
    'admin.recentCats': 'Recent Cats',
    'admin.recentProducts': 'Recent Products',
    'admin.viewAll': 'View all',
    'admin.addNewCat': 'Add New Cat',
    'admin.editCat': 'Edit Cat',
    'admin.addNewProduct': 'Add New Product',
    'admin.editProduct': 'Edit Product',
    'admin.catName': 'Cat Name',
    'admin.ageMonths': 'Age (months)',
    'admin.listingType': 'Listing Type',
    'admin.price': 'Price (EGP)',
    'admin.description': 'Description',
    'admin.images': 'Images',
    'admin.existingImages': 'existing images',
    'admin.newImages': 'new images',
    'admin.maxImages': 'Max 5 total',
    'admin.addImages': 'Add Images',
    'admin.removeImage': 'Remove image',
    'admin.existingImage': 'Existing',
    'admin.imagesSelected': 'images selected',
    'admin.descriptionPlaceholder': 'Enter a description...',
    'admin.cancel': 'Cancel',
    'admin.saveCat': 'Save Cat',
    'admin.updateCat': 'Update Cat',
    'admin.createCat': 'Create Cat',
    'admin.saving': 'Saving...',
    'admin.productName': 'Product Name',
    'admin.category': 'Category',
    'admin.selectCategory': 'Select a category',
    'admin.addNewCategory': '+ Add new category...',
    'admin.newCategoryPlaceholder': 'Type the new category name',
    'admin.stockQuantity': 'Stock Quantity',
    'admin.saveProduct': 'Save Product',
    'admin.updateProduct': 'Update Product',
    'admin.createProduct': 'Create Product',
    'admin.deleteConfirm': 'Are you sure you want to delete this item? This action cannot be undone.',
    'admin.catDeleted': 'Cat deleted successfully',
    'admin.productDeleted': 'Product deleted successfully',
    'admin.deleteFailed': 'Failed to delete. Please try again.',
    'admin.deleting': 'Deleting...',
    'admin.catCreated': 'Cat created successfully',
    'admin.catUpdated': 'Cat updated successfully',
    'admin.productCreated': 'Product created successfully',
    'admin.productUpdated': 'Product updated successfully',
    'admin.saveFailed': 'Failed to save. Please try again.',
    'admin.statusUpdated': 'Status updated successfully',
    'admin.updateFailed': 'Failed to update status.',
    'admin.noCatsYet': 'No cats have been added yet.',
    'admin.noProductsYet': 'No products have been added yet.',
    'admin.addFirstCat': 'Add Your First Cat',
    'admin.addFirstProduct': 'Add Your First Product',
    'admin.backToCats': 'Back to Cats',
    'admin.backToProducts': 'Back to Products',
    'admin.updateCatDesc': 'Update the cat details below',
    'admin.createCatDesc': 'Fill in the details to list a new cat',
    'admin.updateProductDesc': 'Update the product details below',
    'admin.createProductDesc': 'Fill in the details to list a new product',
    'admin.nameRequired': 'Name is required',
    'admin.ageRequired': 'Valid age is required',
    'admin.sexRequired': 'Sex is required',
    'admin.listingTypeRequired': 'Listing type is required',
    'admin.priceRequired': 'Price is required for sale listings',
    'admin.descriptionRequired': 'Description is required',
    'admin.imageRequired': 'At least one image is required',
    'admin.categoryRequired': 'Category is required',
    'admin.stockRequired': 'Valid stock quantity is required',
    'admin.productNamePlaceholder': 'Product name',
    'common.added': 'Added',
    'common.all': 'All',
    'common.message': 'Message',
    'common.products': 'products',
    'admin.priceNotRequired': 'Price not required for adoption listings',
    'admin.accessDenied': 'Access Denied',
    'admin.accessDeniedDesc': 'You must be logged in to access the admin panel.',
    'admin.signIn': 'Sign In',
    'admin.loading': 'Loading...',
    'footer.support': 'Support',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.allCats': 'All Cats',
    'footer.contactUs': 'Contact Us',
    'footer.aboutUs': 'About Us',
    'footer.about': 'About',
    'footer.helpCenter': 'Help Center',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.termsOfService': 'Terms of Service',
    'footer.cookiePolicy': 'Cookie Policy',
    'footer.accessibility': 'Accessibility',
    'footer.rightsReserved': 'All rights reserved.',
    'footer.madeWithCare': 'Designed & Developed by Abdullah Maher',
    'about.title': 'About CAT CARE',
    'about.backToHome': 'Back to Home',
    'about.intro': 'CAT CARE is a leading platform for cat adoption and premium cat products. We connect loving homes with cats in need, while providing everything your feline friend deserves.',
    'about.ourMission': 'Our Mission',
    'about.missionText': 'We believe every cat deserves a loving home. Our adoption-first approach means we prioritize finding the right match between cats and families, ensuring happy endings for both. Combined with health guarantees and fast, safe shipping, we make the journey from shelter to sofa as smooth as possible.',
    'about.whyChooseUs': 'Why Choose Us',
    'about.adoptionFirst': 'Adoption First',
    'about.adoptionFirstDesc': 'Every cat deserves a loving home. We prioritize adoption to give cats a second chance.',
    'about.vetChecked': 'Vet Checked',
    'about.vetCheckedDesc': 'All cats are health-checked before adoption.',
    'about.fastShipping': 'Fast Shipping',
    'about.fastShippingDesc': 'Quick and safe delivery for all cat products across Egypt.',
    'about.trustedPlatform': 'Trusted Platform',
    'about.trustedPlatformDesc': 'Verified listings and secure transactions you can count on.',
    'about.cta': 'Ready to find your purrfect companion?',
    'about.browseCats': 'Browse Cats',
    'about.shopProducts': 'Shop Products',
    'language.english': 'English',
    'language.arabic': 'العربية',
    'language.switchToEnglish': 'Switch to English',
    'language.switchToArabic': 'التحول إلى العربية',
    'theme.light': 'Light',
    'theme.dark': 'Dark',
    'theme.system': 'System',
    'theme.toggle': 'Toggle theme',
    'adoption.title': 'Adoption Request',
    'adoption.phoneNumber': 'Phone Number (WhatsApp)',
    'adoption.message': 'Why do you want to adopt?',
    'adoption.adoptedBefore': 'Have you adopted a cat before?',
    'adoption.submit': 'Submit Adoption Request',
    'adoption.success': 'Your adoption request has been submitted!',
    'adoption.error': 'Failed to submit adoption request',
    'adoption.phonePlaceholder': '1XX XXX XXXX',
    'adoption.messagePlaceholder': 'Tell us why you want to adopt this cat...',
    'adoption.successDesc': 'We\'ll contact you via WhatsApp shortly.',
    'adoption.phoneHint': 'Egypt phone numbers only',
    'adoption.phoneError': 'Please enter a valid Egyptian phone number (e.g. 10XXXXXXXX)',
    'adoption.messageError': 'Please enter a message',
    'adoption.duplicateRequest': 'You have already submitted a request for this cat',
    'adoption.catNotAvailable': 'This cat is not available for adoption',
    'admin.requests.whatsappMsg': 'مرحباً! أنا أتواصل معك بخصوص طلب تبنيك للقطة {catName}. يمكنك الاطلاع عليها هنا: {link}',
    'admin.requests.empty': 'No adoption requests found',
    'admin.requests.emptyFiltered': 'Try adjusting your filter.',
    'admin.requests.emptyAll': 'No adoption requests have been made yet.',
    'admin.requests.clearFilter': 'Clear Filter',
    'admin.requests.adoptedBefore': 'Adopted before',
    'admin.requests.firstTime': 'First time',
    'admin.requests.phoneLabel': 'Phone',
    'admin.requests.viewCat': 'View cat page',
    'purchase.title': 'Order Request',
    'purchase.phoneNumber': 'Phone Number (WhatsApp)',
    'purchase.quantity': 'Quantity',
    'purchase.addToOrder': 'Add to Order',
    'purchase.submit': 'Submit Order',
    'purchase.success': 'Your order has been submitted!',
    'purchase.error': 'Failed to submit order',
    'purchase.phonePlaceholder': '1XX XXX XXXX',
    'purchase.selectStock': 'Select stock',
    'purchase.items': 'Items',
    'purchase.total': 'Total',
    'purchase.noItems': 'No items to order',
    'purchase.orderFor': 'Order for',
    'admin.adoptionRequests': 'Adoption Requests',
    'admin.purchaseRequests': 'Purchase Requests',
    'admin.pendingRequests': 'Pending Requests',
    'admin.noPendingRequests': 'All caught up! No pending requests.',
    'admin.totalRevenue': 'Total Revenue',
    'admin.revenueFromApproved': 'from approved purchases',
    'admin.quickActions': 'Quick Actions',
    'admin.stockAlerts': 'Stock Alerts',
    'admin.stockAlertsDesc': 'Products running low or out of stock',
    'admin.noStockAlerts': 'All products are well stocked',
    'admin.requests.status': 'Status',
    'admin.requests.phone': 'Phone',
    'admin.requests.date': 'Date',
    'admin.requests.actions': 'Actions',
    'admin.requests.view': 'View',
    'admin.requests.whatsapp': 'Open WhatsApp',
    'admin.requests.approve': 'Approve',
    'admin.requests.reject': 'Reject',
    'admin.requests.pending': 'Pending',
    'admin.requests.approved': 'Approved',
    'admin.requests.rejected': 'Rejected',
    'admin.requests.responded': 'Responded',
    'header.theme': 'Theme',
    'header.language': 'Language',
    'header.cart': 'Cart',
    'header.admin': 'Admin',
    'gallery.noImages': 'No images available',
    'gallery.imageOf': 'Image {current} of {total}',
    'gallery.main': 'Image gallery',
    'gallery.prev': 'Previous image',
    'gallery.next': 'Next image',
    'favorites.title': 'My Favorites',
    'favorites.subtitle': 'Cats and products you saved',
    'favorites.empty': 'No favorites yet',
    'favorites.emptyDesc': 'Tap the heart icon on any cat or product to save it here.',
    'offline.recheck': 'Re-check',
    'offline.checking': 'checking',
    'offline.title': 'No Internet Connection',
    'offline.message': 'It looks like you are offline. Please check your network connection and try again.',
    'offline.refresh': 'Refresh Page',
    'error.notFound': 'Not Found',
    'error.notFoundDesc': 'The page you\'re looking for doesn\'t exist or has been removed.',
    'error.catNotFound': 'Cat not found',
    'error.catNotFoundDesc': 'The cat you\'re looking for doesn\'t exist or has been removed.',
    'error.productNotFound': 'Product not found',
    'error.productNotFoundDesc': 'The product you\'re looking for doesn\'t exist or has been removed.',
    'error.failedToLoadCat': 'Failed to load cat details',
    'error.failedToLoadProduct': 'Failed to load product details',
    'productCard.viewDetails': 'View Details',
    'productCard.feeWaived': 'Free',
    'productCard.availableToAdopt': 'Available to adopt',
    'productCard.onlyLeft': 'Only {count} left',
    'productCard.xLeft': '{count} left',
    'bulk.select': 'Select',
    'bulk.selectItems': 'Select items to delete',
    'bulk.itemsSelected': '{count} items selected',
    'bulk.confirmTitle': 'Delete {count} items?',
    'bulk.confirmMessage': 'Are you sure you want to delete {count} {type}? This action cannot be undone.',
    'bulk.confirmCats': 'cat | cats',
    'bulk.confirmProducts': 'product | products',
    'bulk.confirmRequests': 'request | requests',
    'bulk.confirmYes': 'Yes, Delete All',
    'bulk.deleting': 'Deleting...',
    'bulk.deletingProgress': '{done} of {total} deleted',
    'bulk.success': 'Successfully deleted',
    'bulk.successMessage': '{count} {type} deleted successfully.',
    'bulk.partialFail': 'Partially deleted',
    'bulk.partialFailMessage': '{deleted} deleted, {failed} failed.',
    'bulk.failed': 'Delete failed',
    'bulk.failedMessage': 'Failed to delete items. Please try again.',
    'bulk.done': 'Done',
  },
  ar: {
    'nav.home': 'الرئيسية',
    'nav.cats': 'تبني',
    'nav.products': 'المنتجات',
    'nav.cart': 'السلة',
    'nav.login': 'تسجيل الدخول',
    'nav.register': 'إنشاء حساب',
    'nav.profile': 'الملف الشخصي',
    'nav.orders': 'الطلبات',
    'nav.wishlist': 'المفضلة',
    'nav.about': 'عن CAT CARE',
    'nav.logout': 'تسجيل الخروج',
    'nav.admin': 'الإدارة',
    'nav.admin.dashboard': 'لوحة التحكم',
    'nav.admin.cats': 'إدارة القطط',
    'nav.admin.products': 'إدارة المنتجات',
    'nav.admin.orders': 'الطلبات',
    'nav.admin.users': 'المستخدمون',
    'nav.admin.settings': 'الإعدادات',
    'nav.backToStore': 'العودة للمتجر',
    'common.cats': 'القطط',
    'common.loading': 'جاري التحميل...',
    'common.error': 'خطأ',
    'common.retry': 'إعادة المحاولة',
    'common.backToHome': 'العودة للرئيسية',
    'common.goBack': 'رجوع',
    'common.linkCopied': 'تم نسخ الرابط!',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.close': 'إغلاق',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.view': 'عرض',
    'common.add': 'إضافة',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.clear': 'مسح',
    'common.clearAll': 'مسح الكل',
    'common.confirm': 'تأكيد',
    'common.ok': 'حسناً',
    'common.yes': 'نعم',
    'common.no': 'لا',
    'common.or': 'أو',
    'common.and': 'و',
    'common.of': 'من',
    'common.items': 'عناصر',
    'common.item': 'عنصر',
    'common.total': 'المجموع',
    'common.subtotal': 'المجموع الفرعي',
    'common.shipping': 'الشحن',
    'common.tax': 'الضريبة',
    'common.free': 'مجاني',
    'common.price': 'السعر',
    'common.stock': 'المخزون',
    'common.category': 'الفئة',
    'common.description': 'الوصف',
    'common.name': 'الاسم',
    'common.email': 'البريد الإلكتروني',
    'common.password': 'كلمة المرور',
    'common.confirmPassword': 'تأكيد كلمة المرور',
    'common.age': 'العمر',
    'common.sex': 'الجنس',
    'common.male': 'ذكر',
    'common.female': 'أنثى',
    'common.status': 'الحالة',
    'common.actions': 'إجراءات',
    'common.listed': 'تاريخ الإضافة',
    'common.updated': 'تم التحديث',
    'common.created': 'تاريخ الإنشاء',
    'common.available': 'متاح',
    'common.adopted': 'تم التبني',
    'common.sold': 'تم البيع',
    'common.pending': 'قيد الانتظار',
    'common.inStock': 'متوفر',
    'common.lowStock': 'مخزون منخفض',
    'common.outOfStock': 'نفد المخزون',
    'common.forSale': 'للبيع',
    'common.forAdoption': 'للتبني',
    'common.sale': 'بيع',
    'common.adoption': 'تبني',
    'common.adoptionFee': 'رسوم التبني',
    'common.feeWaived': 'تُعفى الرسوم للمتبنين المؤهلين',
    'common.months': 'أشهر',
    'common.month': 'شهر',
    'common.monthsOld': '{count} أشهر',
    'common.filtered': '(مُصفى)',
    'common.showing': 'عرض {count} من {total}',
    'common.egp': 'ج.م',
    'common.currency': 'ج.م',
    'common.allStatus': 'كل الحالات',
    'common.allTypes': 'جميع الأنواع',
    'common.type': 'النوع',
    'common.cat': 'قطة',
    'common.product': 'منتج',
    'common.approved': 'تمت الموافقة',
    'common.rejected': 'مرفوض',
    'common.preview': 'معاينة',
    'common.share': 'مشاركة',
    'common.whatsapp': 'واتساب',
    'common.searchCats': 'ابحث عن القطط بالاسم...',
    'common.clearSearch': 'مسح البحث',
    'common.viewOnStore': 'عرض في المتجر',
    'common.editCat': 'تعديل القط',
    'common.deleteCat': 'حذف القط',
    'common.editProduct': 'تعديل المنتج',
    'common.deleteProduct': 'حذف المنتج',
    'common.addToFavorites': 'أضف إلى المفضلة',
    'common.removeFromFavorites': 'إزالة من المفضلة',
    'common.noCatsFound': 'لا توجد قطط',
    'common.noProductsFound': 'لا توجد منتجات',
    'common.tryAdjusting': 'حاول تعديل البحث أو الفلاتر.',
    'common.ageMonths': '{count} شهر',
    'home.title': 'تبنى قطة. أنقذ حياة.',
    'home.subtitle': 'آلاف القطط تحتاج منازل محبة. تبني قطة اليوم أو تسوق المنتجات المميزة لصديقك القططي.',
    'home.browseCats': 'تبني الآن',
    'home.shopProducts': 'تسوق المنتجات',
    'home.trustedBy': 'يثق بنا أكثر من 10,000 محب للقطط',
    'home.features.adoptionFirst': 'التبني أولاً',
    'home.features.adoptionFirstDesc': 'نحن نعطي الأولوية لإيجاد منازل محبة دائمة لكل قطط رعايتنا.',
    'home.features.vetChecked': 'فحص بيطري',
    'home.features.vetCheckedDesc': 'جميع القطط تتم مراجعتها قبل العرض.',
    'home.features.shipping': 'شحن موثوق',
    'home.features.shippingDesc': 'توصيل سريع وآمن مباشرة إلى بابك مع تغليف آمن.',
    'home.catsAvailable': 'قطط للتبني',
    'home.catsSubtitle': 'هذه القطط تنتظر منزلاً محبباً',
    'home.adoptCatsCta': 'تبني قطة',
    'home.forSaleCats': 'قطط للبيع',
    'home.forSaleCatsSubtitle': 'قطط سلالات مميزة متاحة للشراء',
    'home.featuredProducts': 'منتجات مميزة',
    'home.productsSubtitle': 'أساسيات لصديقك القططي',
    'home.viewAll': 'عرض الكل',
    'home.noListings': 'لا توجد قوائم بعد',
    'home.noListingsDesc': 'كن أول من يضيف قطة أو منتج. ابدأ ببناء مجتمعنا!',
    'home.addCat': 'إضافة قطة',
    'home.addProduct': 'إضافة منتج',
    'home.whatsappTitle': 'لديك قطة للتبني؟',
    'home.whatsappDesc': 'أضف قطتك على كات كير وابحث لها عن بيت دافء. تواصل معنا عبر واتساب للبدء.',
    'home.whatsappCta': 'تواصل عبر واتساب',
    'home.listCatCta': 'اعرض قطتك للتبني',
    'home.whatsappMsg': 'مرحباً كت كير! عايز أعرض قطة للتبني على منصتكم.',
    'cats.title': 'قطط',
    'cats.toggleAdoption': 'للتبني',
    'cats.toggleSale': 'للبيع',
    'cats.toggleAll': 'للكل',
    'cats.found': 'قطة تم العثور عليها',
    'cats.cat': 'قطة',
    'cats.sortBy': 'ترتيب حسب',
    'cats.sort.newest': 'الأحدث أولاً',
    'cats.sort.oldest': 'الأقدم أولاً',
    'cats.sort.priceAsc': 'السعر: من الأقل للأعلى',
    'cats.sort.priceDesc': 'السعر: من الأعلى للأقل',
    'cats.sort.ageAsc': 'العمر: الأصغر أولاً',
    'cats.sort.ageDesc': 'العمر: الأكبر أولاً',
    'cats.filter.listingType': 'نوع العرض',
    'cats.filter.allTypes': 'جميع الأنواع',
    'cats.filter.sale': 'للبيع',
    'cats.filter.adoption': 'للتبني',
    'cats.filter.sex': 'الجنس',
    'cats.filter.all': 'الكل',
    'cats.noCatsFound': 'لم يتم العثور على قطط',
    'cats.noCatsFoundFiltered': 'حاول تعديل المرشحات للعثور على المزيد من القطط.',
    'cats.noCatsAvailable': 'لا توجد قطط متاحة حالياً. تحقق لاحقاً!',
    'cats.clearFilters': 'مسح المرشحات',
    'products.title': 'المنتجات',
    'products.found': 'منتج تم العثور عليه',
    'products.product': 'منتج',
    'products.sortBy': 'ترتيب حسب',
    'products.sort.newest': 'الأحدث أولاً',
    'products.sort.oldest': 'الأقدم أولاً',
    'products.sort.priceAsc': 'السعر: من الأقل للأعلى',
    'products.sort.priceDesc': 'السعر: من الأعلى للأقل',
    'products.sort.nameAsc': 'الاسم: من أ إلى ي',
    'products.sort.nameDesc': 'الاسم: من ي إلى أ',
    'products.search': 'البحث عن منتجات...',
    'products.filter.category': 'الفئة',
    'products.filter.allCategories': 'جميع الفئات',
    'products.filter.stock': 'حالة المخزون',
    'products.filter.allStock': 'كل المخزون',
    'products.filter.inStock': 'متوفر (10+)',
    'products.filter.lowStock': 'مخزون منخفض (1-9)',
    'products.filter.outOfStock': 'نفد المخزون',
    'products.noProductsFound': 'لم يتم العثور على منتجات',
    'products.noProductsFoundFiltered': 'حاول تعديل المرشحات أو مصطلحات البحث.',
    'products.noProductsAvailable': 'لا توجد منتجات متاحة حالياً.',
    'products.clearFilters': 'مسح المرشحات',
    'catDetail.backToCats': 'العودة للقطط',
    'catDetail.about': 'حول',
    'catDetail.details': 'التفاصيل',
    'catDetail.age': 'العمر',
    'catDetail.listed': 'تاريخ الإضافة',
    'catDetail.location': 'الموقع',
    'catDetail.availableLocally': 'متاح محلياً',
    'catDetail.id': 'المعرف',
    'catDetail.noDescription': 'لا يوجد وصف متاح لهذه القطة.',
    'catDetail.purchaseDesc': 'مستعد للشراء؟ سيتم حجز هذه القطة لك عند الدفع.',
    'catDetail.adoptionDesc': 'مهتم بالتبني؟ تواصل معنا لبدء عملية التبني.',
    'catDetail.addToCart': 'أضف للسلة - شراء',
    'catDetail.startAdoption': 'بدء عملية التبني',
    'catDetail.notAvailable': 'غير متاح',
    'catDetail.addedToCart': 'تمت الإضافة للسلة',
    'catDetail.localPickup': 'الاستلام المحلي',
    'catDetail.arrangePickup': 'ترتيب الاستلام',
    'catDetail.healthGuaranteed': 'ضمان الصحة',
    'catDetail.vetChecked': 'فحوص بيطرية',
    'catDetail.viewImage': 'عرض صورة',
    'catDetail.orderNow': 'اطلب الآن',
    'catDetail.adoptNow': 'تبني الآن',
    'productDetail.backToProducts': 'العودة للمنتجات',
    'productDetail.description': 'الوصف',
    'productDetail.quantity': 'الكمية',
    'productDetail.subtotal': 'المجموع الفرعي',
    'productDetail.addToCart': 'أضف للسلة',
    'productDetail.updateCart': 'تحديث السلة',
    'productDetail.outOfStock': 'نفد المخزون',
    'productDetail.inCart': 'في السلة',
    'productDetail.alreadyInCart': 'موجود بالفعل في السلة',
    'productDetail.addedToCart': 'تمت الإضافة للسلة',
    'productDetail.category': 'الفئة',
    'productDetail.stockAvailable': 'المخزون المتاح',
    'productDetail.units': 'وحدات',
    'productDetail.sku': 'رمز المنتج',
    'productDetail.added': 'تاريخ الإضافة',
    'productDetail.viewImage': 'عرض صورة',
    'productDetail.decreaseQty': 'تقليل الكمية',
    'productDetail.increaseQty': 'زيادة الكمية',
    'productDetail.orderNow': 'اطلب الآن',
    'productDetail.productDetails': 'تفاصيل المنتج',
    'productDetail.qualityGuaranteed': 'جودة مضمونة',
    'productDetail.vetChecked': 'فحص بيطرى',
    'productDetail.premiumProducts': 'منتجات متميزة',
    'cart.title': 'سلة التسوق',
    'cart.empty': 'سلتك فارغة',
    'cart.emptyDesc': 'يبدو أنك لم تضف أي شيء بعد. ابدأ التسوق لصديقك القططي!',
    'cart.browseCats': 'تصفح القطط',
    'cart.shopProducts': 'تسوق المنتجات',
    'cart.cats': 'القطط',
    'cart.products': 'المنتجات',
    'cart.subtotal': 'المجموع الفرعي',
    'cart.shipping': 'الشحن',
    'cart.tax': 'الضريبة',
    'cart.total': 'المجموع',
    'cart.freeShipping': 'أنت مؤهل للشحن المجاني!',
    'cart.addForFreeShipping': 'أضف ${amount} إضافياً للشحن المجاني!',
    'cart.checkout': 'الانتقال للدفع',
    'cart.clearCart': 'مسح السلة',
    'cart.continueShopping': 'مواصلة التسوق',
    'cart.secure': 'آمن',
    'cart.fast': 'سريع',
    'cart.placeOrder': 'تقديم الطلب',
    'cart.adoptionRequests': 'طلب تبني',
    'cart.purchaseRequests': 'طلب شراء',
    'cart.removeItem': 'إزالة العنصر',
    'auth.welcomeBack': 'أهلاً بعودتك',
    'auth.signInDesc': 'سجل الدخول إلى حسابك للمتابعة',
    'auth.createAccount': 'إنشاء حسابك',
    'auth.registerDesc': 'انضم لمجتمع محبي القطط',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.confirmPassword': 'تأكيد كلمة المرور',
    'auth.signIn': 'تسجيل الدخول',
    'auth.createAccountBtn': 'إنشاء حساب',
    'auth.forgotPassword': 'نسيت كلمة المرور؟',
    'auth.dontHaveAccount': 'ليس لديك حساب؟',
    'auth.alreadyHaveAccount': 'لديك حساب بالفعل؟',
    'auth.createOne': 'أنشئ واحداً',
    'auth.signInLink': 'تسجيل الدخول',
    'auth.termsAgree': 'بإنشاء حساب، أنت توافق على',
    'auth.termsOfService': 'شروط الخدمة',
    'auth.privacyPolicy': 'سياسة الخصوصية',
    'auth.passwordMismatch': 'كلمات المرور غير متطابقة',
    'auth.passwordTooShort': 'كلمة المرور يجب أن تكون 8 أحرف على الأقل',
    'auth.invalidCredentials': 'بريد إلكتروني أو كلمة مرور غير صحيحة',
    'auth.registrationFailed': 'فشل التسجيل. يرجى المحاولة مرة أخرى.',
    'admin.deletProduct': 'حذف المنتج',
    'admin.dashboard': 'لوحة التحكم',
    'admin.welcome': 'أهلاً بعودتك! إليك ما يحدث مع متجرك.',
    'admin.loadFailed': 'فشل تحميل لوحة التحكم',
    'admin.loadFailedDesc': 'تعذر جلب بيانات لوحة التحكم. تحقق من اتصالك وحاول مرة أخرى.',
    'admin.totalCats': 'إجمالي القطط',
    'admin.availableCats': 'متاح',
    'admin.pendingCats': 'تبني قيد الانتظار',
    'admin.totalProducts': 'المنتجات',
    'admin.lowStockProducts': 'مخزون منخفض',
    'admin.addCat': 'إضافة قطة',
    'admin.addProduct': 'إضافة منتج',
    'admin.viewOrders': 'عرض الطلبات',
    'admin.manageUsers': 'إدارة المستخدمين',
    'admin.manageCats': 'إدارة القطط',
    'admin.totalCatsCount': '{count} قطط إجمالاً',
    'admin.manageProducts': 'إدارة المنتجات',
    'admin.recentActivity': 'النشاط الأخير',
    'admin.recentCats': 'قطط حديثة',
    'admin.recentProducts': 'منتجات حديثة',
    'admin.viewAll': 'عرض الكل',
    'admin.addNewCat': 'إضافة قطة جديدة',
    'admin.editCat': 'تعديل القطة',
    'admin.addNewProduct': 'إضافة منتج جديد',
    'admin.editProduct': 'تعديل المنتج',
    'admin.catName': 'اسم القطة',
    'admin.ageMonths': 'العمر (أشهر)',
    'admin.listingType': 'نوع العرض',
    'admin.price': 'السعر (ج.م)',
    'admin.description': 'الوصف',
    'admin.images': 'الصور',
    'admin.existingImages': 'الصور الحالية',
    'admin.newImages': 'صور جديدة',
    'admin.maxImages': 'الحد الأقصى 5',
    'admin.addImages': 'إضافة صور',
    'admin.removeImage': 'إزالة الصورة',
    'admin.existingImage': 'حالية',
    'admin.imagesSelected': 'صور محددة',
    'admin.descriptionPlaceholder': 'أدخل وصفاً...',
    'admin.cancel': 'إلغاء',
    'admin.saveCat': 'حفظ القطة',
    'admin.updateCat': 'تحديث القطة',
    'admin.createCat': 'إنشاء قطة',
    'admin.saving': 'جاري الحفظ...',
    'admin.productName': 'اسم المنتج',
    'admin.category': 'الفئة',
    'admin.selectCategory': 'اختر فئة',
    'admin.addNewCategory': '+ إضافة فئة جديدة...',
    'admin.newCategoryPlaceholder': 'اكتب اسم الفئة الجديدة',
    'admin.stockQuantity': 'كمية المخزون',
    'admin.saveProduct': 'حفظ المنتج',
    'admin.updateProduct': 'تحديث المنتج',
    'admin.createProduct': 'إنشاء منتج',
    'admin.deleteConfirm': 'هل أنت متأكد من حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.',
    'admin.catDeleted': 'تم حذف القط بنجاح',
    'admin.productDeleted': 'تم حذف المنتج بنجاح',
    'admin.deleteFailed': 'فشل في الحذف. يرجى المحاولة مرة أخرى.',
    'admin.delete': 'حذف',
    'admin.deleteProduct': 'حذف المنتج',
    'admin.deleteProductConfirm': 'سيتم حذف هذا المنتج نهائياً. هذا الإجراء لا يمكن التراجع عنه.',
    'admin.deleteCat': 'حذف القط',
    'admin.deleteCatConfirm': 'سيتم حذف هذا القط نهائياً. هذا الإجراء لا يمكن التراجع عنه.',
    'admin.deleting': 'جاري الحذف...',
    'admin.catCreated': 'تم إنشاء القطة بنجاح',
    'admin.catUpdated': 'تم تحديث القطة بنجاح',
    'admin.productCreated': 'تم إنشاء المنتج بنجاح',
    'admin.productUpdated': 'تم تحديث المنتج بنجاح',
    'admin.saveFailed': 'فشل في الحفظ. يرجى المحاولة مرة أخرى.',
    'admin.statusUpdated': 'تم تحديث الحالة بنجاح',
    'admin.updateFailed': 'فشل في تحديث الحالة.',
    'admin.noCatsYet': 'لم تتم إضافة أي قطط بعد.',
    'admin.noProductsYet': 'لم تتم إضافة أي منتجات بعد.',
    'admin.addFirstCat': 'إضافة قطتك الأولى',
    'admin.addFirstProduct': 'إضافة منتجك الأول',
    'admin.backToCats': 'العودة للقطط',
    'admin.backToProducts': 'العودة للمنتجات',
    'admin.updateCatDesc': 'تعديل تفاصيل القطة أدناه',
    'admin.createCatDesc': 'املأ التفاصيل لإضافة قطة جديدة',
    'admin.updateProductDesc': 'تعديل تفاصيل المنتج أدناه',
    'admin.createProductDesc': 'املأ التفاصيل لإضافة منتج جديد',
    'admin.nameRequired': 'الاسم مطلوب',
    'admin.ageRequired': 'العمر مطلوب',
    'admin.sexRequired': 'الجنس مطلوب',
    'admin.listingTypeRequired': 'نوع القائمة مطلوب',
    'admin.priceRequired': 'السعر مطلوب للبيع',
    'admin.descriptionRequired': 'الوصف مطلوب',
    'admin.imageRequired': 'الصورة مطلوبة',
    'admin.categoryRequired': 'الفئة مطلوبة',
    'admin.stockRequired': 'كمية المخزون مطلوبة',
    'admin.productNamePlaceholder': 'اسم المنتج',
    'common.added': 'تمت الإضافة',
    'common.all': 'الكل',
    'common.message': 'الرسالة',
    'common.products': 'منتجات',
    'admin.priceNotRequired': 'السعر غير مطلوب للتبني',
    'admin.accessDenied': 'تم رفض الوصول',
    'admin.accessDeniedDesc': 'يجب عليك تسجيل الدخول للوصول إلى لوحة التحكم.',
    'admin.signIn': 'تسجيل الدخول',
    'admin.loading': 'جاري التحميل...',
    'footer.support': 'الدعم',
    'footer.company': 'الشركة',
    'footer.legal': 'قانوني',
    'footer.allCats': 'جميع القطط',
    'footer.contactUs': 'اتصل بنا',
    'footer.aboutUs': 'من نحن',
    'footer.about': 'حول',
    'footer.helpCenter': 'مركز المساعدة',
    'footer.privacyPolicy': 'سياسة الخصوصية',
    'footer.termsOfService': 'شروط الخدمة',
    'footer.cookiePolicy': 'سياسة ملفات تعريف الارتباط',
    'footer.accessibility': 'إمكانية الوصول',
    'footer.rightsReserved': 'جميع الحقوق محفوظة.',
    'footer.madeWithCare': 'صُمِّم وطُوِّر بواسطة: عبدالله ماهر',
    'about.title': 'عن CAT CARE',
    'about.backToHome': 'العودة للرئيسية',
    'about.intro': 'CAT CARE هي منصة رائدة في مصر لتبني القطط ومنتجاتها المميزة. نربط بين المنازل المحبة والقطط التي تحتاج إلى عائلة، مع توفير كل ما يحتاجه صديكك القطني.',
    'about.ourMission': 'مهمتنا',
    'about.missionText': 'نؤمن بأن كل قطة تستحق منزلاً محبًا. نهجنا الذي يضع التبني أولاً يعني أننا نحرص على إيجاد التطابق الصحيح بين القطط والعائلات، مما يضمن نهايات سعيدة للطرفين. وبالإضافة إلى ضمانات الصحة والشحن السريع والآمن، نجعل رحلة الانتقال من المأوى إلى الأريكة سلسة قدر الإمكان.',
    'about.whyChooseUs': 'لماذا تختارنا',
    'about.adoptionFirst': 'التبني أولاً',
    'about.adoptionFirstDesc': 'كل قطة تستحق منزلاً محبًا. نعطي الأولوية للتبني لإعطاء القطط فرصة ثانية.',
    'about.vetChecked': 'فحوصات بيطرية',
    'about.vetCheckedDesc': 'جميع القطط مفحوصة صحياً قبل التبني.',
    'about.fastShipping': 'شحن سريع',
    'about.fastShippingDesc': 'توصيل سريع وآمن لجميع منتجات القطط في مصر.',
    'about.trustedPlatform': 'منصة موثوقة',
    'about.trustedPlatformDesc': 'قوائم موثقة ومعاملات آمنة يمكنك الاعتماد عليها.',
    'about.cta': 'مستعد للعثور على رفيقك المثالي؟',
    'about.browseCats': 'تصفح القطط',
    'about.shopProducts': 'تسوق المنتجات',
    'language.english': 'English',
    'language.arabic': 'العربية',
    'language.switchToEnglish': 'Switch to English',
    'language.switchToArabic': 'التحول إلى العربية',
    'theme.light': 'فاتح',
    'theme.dark': 'داكن',
    'theme.system': 'النظام',
    'theme.toggle': 'تغيير المظهر',
    'adoption.title': 'طلب تبني',
    'adoption.phoneNumber': 'رقم الهاتف (واتساب)',
    'adoption.message': 'لماذا تريد التبني؟',
    'adoption.adoptedBefore': 'هل قمت بالتبني من قبل؟',
    'adoption.submit': 'إرسال طلب التبني',
    'adoption.success': 'تم إرسال طلب التبني بنجاح!',
    'adoption.error': 'فشل في إرسال طلب التبني',
    'adoption.phonePlaceholder': '1XX XXX XXXX',
    'adoption.messagePlaceholder': 'أخبرنا لماذا تريد تبني هذه القطة...',
    'adoption.successDesc': 'سنتواصل معك عبر واتساب قريباً.',
    'adoption.phoneHint': 'أرقامهواتف مصرية فقط',
    'adoption.phoneError': 'يرجى إدخال رقم هاتف مصري صحيح (مثال: 10XXXXXXXX)',
    'adoption.messageError': 'يرجى إدخال رسالة',
    'adoption.duplicateRequest': 'لقد قدمت طلب تبني لهذه القطة من قبل',
    'adoption.catNotAvailable': 'هذه القطة غير متاحة للتبني',
    'admin.requests.whatsappMsg': 'مرحباً! أنا أتواصل معك بخصوص طلب تبنيك للقطة {catName}. يمكنك الاطلاع عليها هنا: {link}',
    'admin.requests.empty': 'لم يتم العثور على طلبات تبني',
    'admin.requests.emptyFiltered': 'حاول تعديل الفلتر.',
    'admin.requests.emptyAll': 'لم يتم تقديم أي طلبات تبني بعد.',
    'admin.requests.clearFilter': 'مسح الفلتر',
    'admin.requests.adoptedBefore': 'تبني من قبل',
    'admin.requests.firstTime': 'أول مرة',
    'admin.requests.phoneLabel': 'الهاتف',
    'admin.requests.viewCat': 'عرض صفحة القطة',
    'purchase.title': 'طلب شراء',
    'purchase.phoneNumber': 'رقم الهاتف (واتساب)',
    'purchase.quantity': 'الكمية',
    'purchase.addToOrder': 'أضف للطلب',
    'purchase.submit': 'إرسال الطلب',
    'purchase.success': 'تم إرسال طلبك بنجاح!',
    'purchase.error': 'فشل في إرسال الطلب',
    'purchase.phonePlaceholder': '1XX XXX XXXX',
    'purchase.selectStock': 'اختر الكمية',
    'purchase.items': 'العناصر',
    'purchase.total': 'المجموع',
    'purchase.noItems': 'لا توجد عناصر للطلب',
    'purchase.orderFor': 'طلب لـ',
    'admin.adoptionRequests': 'طلبات التبني',
    'admin.purchaseRequests': 'طلبات الشراء',
    'admin.pendingRequests': 'الطلبات المعلقة',
    'admin.noPendingRequests': 'لا توجد طلبات معلقة!',
    'admin.totalRevenue': 'إجمالي الإيرادات',
    'admin.revenueFromApproved': 'من الطلبات المعتمدة',
    'admin.quickActions': 'إجراءات سريعة',
    'admin.stockAlerts': 'تنبيهات المخزون',
    'admin.stockAlertsDesc': 'منتجات نفدت أو اقتربت من النفاد',
    'admin.noStockAlerts': 'جميع المنتجات متوفرة بكميات كافية',
    'admin.requests.status': 'الحالة',
    'admin.requests.phone': 'الهاتف',
    'admin.requests.date': 'التاريخ',
    'admin.requests.actions': 'إجراءات',
    'admin.requests.view': 'عرض',
    'admin.requests.whatsapp': 'فتح واتساب',
    'admin.requests.approve': 'موافقة',
    'admin.requests.reject': 'رفض',
    'admin.requests.pending': 'قيد الانتظار',
    'admin.requests.approved': 'موافق عليه',
    'admin.requests.rejected': 'مرفوض',
    'admin.requests.responded': 'تم الرد',
    'header.theme': 'المظهر',
    'header.language': 'اللغة',
    'header.cart': 'السلة',
    'header.admin': 'الإدارة',
    'gallery.noImages': 'لا توجد صور متاحة',
    'gallery.imageOf': 'صورة {current} من {total}',
    'gallery.main': 'معرض الصور',
    'gallery.prev': 'الصورة السابقة',
    'gallery.next': 'الصورة التالية',
    'favorites.title': 'مفضلاتي',
    'favorites.subtitle': 'القطط والمنتجات التي حفظتها',
    'favorites.empty': 'لا توجد مفضلات بعد',
    'favorites.emptyDesc': 'اضغط على أيقونة القلب على أي قطة أو منتج لحفظه هنا.',
    'offline.recheck': 'إعادة التحقق',
    'offline.checking': 'جارٍ التحقق...',
    'offline.title': 'لا يوجد اتصال بالإنترنت',
    'offline.message': 'يبدو أنك غير متصل بالإنترنت. يرجى التحقق من اتصال الشبكة والمحاولة مرة أخرى.',
    'offline.refresh': 'تحديث الصفحة',
    'error.notFound': 'غير موجود',
    'error.notFoundDesc': 'الصفحة التي تبحث عنها غير موجودة أو تمت إزالتها.',
    'error.catNotFound': 'القطة غير موجودة',
    'error.catNotFoundDesc': 'القطة التي تبحث عنها غير موجودة أو تمت إزالتها.',
    'error.productNotFound': 'المنتج غير موجود',
    'error.productNotFoundDesc': 'المنتج الذي تبحث عنه غير موجود أو تمت إزالتها.',
    'error.failedToLoadCat': 'فشل في تحميل بيانات القط',
    'error.failedToLoadProduct': 'فشل في تحميل بيانات المنتج',
    'productCard.viewDetails': 'عرض التفاصيل',
    'productCard.feeWaived': 'مجاني',
    'productCard.availableToAdopt': 'متاح للتبني',
    'productCard.onlyLeft': 'فقط {count} متبقية',
    'productCard.xLeft': '{count} متبقية',
    'bulk.select': 'تحديد',
    'bulk.selectItems': 'حدد العناصر المراد حذفها',
    'bulk.itemsSelected': '{count} عناصر محددة',
    'bulk.confirmTitle': 'حذف {count} عنصر؟',
    'bulk.confirmMessage': 'هل أنت متأكد من حذف {count} {type}؟ لا يمكن التراجع عن هذا الإجراء.',
    'bulk.confirmCats': 'قطة | قطط',
    'bulk.confirmProducts': 'منتج | منتجات',
    'bulk.confirmRequests': 'طلب | طلبات',
    'bulk.confirmYes': 'نعم، حذف الكل',
    'bulk.deleting': 'جاري الحذف...',
    'bulk.deletingProgress': '{done} من {total} تم حذفه',
    'bulk.success': 'تم الحذف بنجاح',
    'bulk.successMessage': 'تم حذف {count} {type} بنجاح.',
    'bulk.partialFail': 'حذف جزئي',
    'bulk.partialFailMessage': 'تم حذف {deleted}، فشل {failed}.',
    'bulk.failed': 'فشل الحذف',
    'bulk.failedMessage': 'فشل في حذف العناصر. يرجى المحاولة مرة أخرى.',
    'bulk.done': 'تم',
  },
};

function readStoredLanguage(): Language {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = localStorage.getItem('language') as Language | null;
    if (saved) return saved;
  } catch {
    // storage unavailable (private mode / disabled) — fall through to default
  }
  try {
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'ar') return 'ar';
  } catch {
    // fall through
  }
  return 'en';
}

function applyLanguageToDocument(language: Language) {
  if (typeof document === 'undefined') return;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  useEffect(() => {
    applyLanguageToDocument(language);
    try {
      localStorage.setItem('language', language);
    } catch {
      // storage unavailable — persist silently is fine
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = translations[language][key];

    if (!translation) {
      translation = key;
    }

    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        translation = translation.replace(new RegExp(`\\{${param}\\}`, 'g'), String(value));
      });
    }

    return translation;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, isRTL: language === 'ar' }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
